import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

interface SelectOrCreateDropdownProps {
  label: string;
  value: string;
  /** Return a Promise when persisting (e.g. C314); the Add button shows a loader until it settles. */
  onChange: (value: string) => void | Promise<void>;
  existingOptions: string[];
  /** When false, only listed options can be chosen (no “Add new”). Default true. */
  allowCreate?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export function SelectOrCreateDropdown({
  label,
  value,
  onChange,
  existingOptions,
  allowCreate = true,
  placeholder = 'Select or type to add new',
  disabled = false,
  required = false,
  className = ''
}: SelectOrCreateDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if current value is in existing options
  const isExistingValue = existingOptions.some(opt => opt.toLowerCase() === value.toLowerCase());
  const displayValue = isExistingValue ? value : (value || '');

  useEffect(() => {
    if (isOpen && isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, isCreating]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (createSubmitting) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsCreating(false);
        setInputValue('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, createSubmitting]);

  const handleSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setIsCreating(false);
    setInputValue('');
  };

  const handleCreate = async () => {
    const raw = inputValue.trim();
    if (!raw || createSubmitting) return;
    setCreateSubmitting(true);
    try {
      await Promise.resolve(onChange(raw));
      setIsOpen(false);
      setIsCreating(false);
      setInputValue('');
    } catch {
      /* Parent is expected to surface errors (e.g. alert). */
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isCreating) {
      e.preventDefault();
      void handleCreate();
    } else if (e.key === 'Escape') {
      if (createSubmitting) return;
      setIsOpen(false);
      setIsCreating(false);
      setInputValue('');
    }
  };

  const filteredOptions = existingOptions.filter(opt => 
    opt.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-destructive-action">*</span>}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && !createSubmitting && setIsOpen(!isOpen)}
          disabled={disabled || createSubmitting}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-between ${
            !displayValue ? 'text-gray-400' : 'text-gray-900'
          }`}
        >
          <span className="truncate">{displayValue || placeholder}</span>
          <ChevronDown 
            size={16} 
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            {!isCreating ? (
              <>
                {existingOptions.length > 0 && (
                  <div className="py-1">
                    {existingOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors ${
                          value === option ? 'bg-action-blue/10 text-action-blue-dark' : 'text-gray-900'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
                {allowCreate ? (
                  <div className="border-t border-gray-200 py-1">
                    <button
                      type="button"
                      onClick={() => setIsCreating(true)}
                      className="w-full text-left px-3 py-2 text-sm text-action-blue hover:bg-action-blue/10 transition-colors font-medium"
                    >
                      + Add new: "{inputValue || '...'}"
                    </button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="p-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter new value"
                  disabled={createSubmitting}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-action-blue focus:border-action-blue disabled:bg-gray-100 disabled:cursor-not-allowed"
                  autoFocus
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (createSubmitting) return;
                      setIsCreating(false);
                      setInputValue('');
                    }}
                    disabled={createSubmitting}
                    className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCreate()}
                    disabled={!inputValue.trim() || createSubmitting}
                    className="inline-flex items-center justify-center gap-2 px-3 py-1 text-sm text-white bg-action-blue rounded hover:bg-action-blue-dark transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[5.5rem]"
                  >
                    {createSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                        <span>Saving…</span>
                      </>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
